import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageTitleContext } from '../../__root';
import { TrashTable } from '../../../features/media-units/components/trash-table';
import { trashListQuery } from '../../../features/media-units/api/queries';
import { useRestoreMediaUnit } from '../../../features/media-units/api/mutations';
import type { MediaUnitTrashItem } from '@ooh/contracts';

// ─── Route ────────────────────────────────────────────────────

export const Route = createFileRoute('/media-units/trash/')({
  beforeLoad: ({ context }) => {
    const perms = context.auth.permissions;
    if (perms !== null && !perms.includes('media_unit.edit')) {
      throw redirect({ to: '/media-units' });
    }
  },
  component: TrashPage,
});

// ─── Page ─────────────────────────────────────────────────────

export function TrashPage() {
  const { t } = useTranslation('inventory');
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { auth } = useRouteContext({ from: '__root__' });
  const { data, isLoading } = useQuery(trashListQuery(auth));
  const restoreMutation = useRestoreMediaUnit();

  React.useEffect(() => {
    setPageTitle(t('trash.pageTitle'));
  }, [setPageTitle, t]);

  const items: MediaUnitTrashItem[] = data?.data ?? [];

  function handleRestore(id: number) {
    restoreMutation.mutate(
      { id },
      {
        onSuccess: () => {
          toast.success(t('trash.restore.success'));
        },
        onError: () => {
          toast.error(t('trash.restore.error'));
        },
      },
    );
  }

  if (isLoading) {
    return null;
  }

  return (
    <TrashTable
      items={items}
      isRestorePending={restoreMutation.isPending}
      onRestore={handleRestore}
    />
  );
}
